/* ---------------------------------------------------------------------------
   Kankatala — currency switcher, OPEN state.

   The closed trigger stays BUCKS's own element. This replaces only the panel
   that opens: a 290px dropdown on desktop, a bottom sheet on mobile/tablet.

   WHERE THE ROWS COME FROM
   Not Shopify. This store's two markets are both INR with local currencies
   off, so `localization.available_countries` has a single entry; the seven
   currencies shoppers see belong to BUCKS, a display-only converter. So the
   rows are READ OUT OF BUCKS's own injected list at runtime: nothing is
   hard-coded here and the list cannot drift from the BUCKS dashboard.
   Choosing a row clicks the matching BUCKS row, so the app's own switching,
   persistence and re-conversion all still run.

   Open/closed state is BUCKS's too: it toggles `.active` on its trigger
   (`.deactive.active` while closing). A MutationObserver mirrors that onto
   our panel, which means the trigger keeps its own click handling, chevron
   rotation and outside-click behaviour.

   Symbols come from Intl, not a table, so a currency added in the BUCKS
   dashboard needs no theme change.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 1024px) and (hover: hover) and (pointer: fine)';

  function isDesktop() {
    return window.matchMedia(DESKTOP).matches;
  }

  /* "US Dollar (USD)" -> { name: "US Dollar", code: "USD" }. BUCKS renders the
     code in brackets after the name; without brackets the whole string is the
     name and the code is recovered from the row's value attributes. */
  function parseLabel(text) {
    var clean = (text || '').replace(/\s+/g, ' ').trim();
    var match = clean.match(/^(.*?)\s*\(([A-Z]{3})\)\s*$/);
    if (match) return { name: match[1].trim(), code: match[2] };
    return { name: clean, code: '' };
  }

  function codeOf(li, name) {
    var attr = li.getAttribute('rel') || li.getAttribute('data-value') || li.getAttribute('value') || '';
    var match = String(attr).match(/[A-Z]{3}/);
    if (match) return match[0];
    var fromName = String(name).match(/\b([A-Z]{3})\b/);
    return fromName ? fromName[1] : '';
  }

  /* Narrow symbol via Intl: "$", "€", "₹", "د.إ". Falls back to the code. */
  function symbolOf(code) {
    if (!code) return '';
    try {
      var parts = new Intl.NumberFormat('en', {
        style: 'currency',
        currency: code,
        currencyDisplay: 'narrowSymbol'
      }).formatToParts(0);
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type === 'currency') return parts[i].value;
      }
    } catch (error) {
      /* Unknown code, or no narrowSymbol support: fall through. */
    }
    return code;
  }

  /* The design shortens one name; everything else is BUCKS's own wording. */
  function shortName(name) {
    return name.replace(/^United Arab Emirates\b/i, 'UAE');
  }

  function bucksRows() {
    var list = document.querySelector('.buckscc-select-options');
    if (!list) return [];

    return Array.prototype.slice.call(list.querySelectorAll('li'))
      .filter(function (li) {
        return li.getAttribute('rel') !== 'hide' && li.textContent.trim() !== '';
      })
      .map(function (li) {
        var parsed = parseLabel(li.textContent);
        var code = parsed.code || codeOf(li, parsed.name);
        return {
          source: li,
          code: code,
          name: shortName(parsed.name),
          symbol: symbolOf(code)
        };
      });
  }

  function currentCode() {
    var trigger = document.querySelector('.buckscc-select-styled');
    var match = trigger ? trigger.textContent.match(/[A-Z]{3}/) : null;
    if (match) return match[0];
    var meta = window.bucksCC && window.bucksCC.metaConfig;
    return (meta && meta.userCurrency) || '';
  }

  /* `hxoFlag-traditional` / `hxoFlag-modern` / `hxoFlag-circle` live on the
     BUCKS root and decide which sprite image and shape the flags use. Our
     panels sit outside that root, so the classes are copied across. */
  function flagStyleClasses() {
    var root = document.querySelector('.hxoSelectTheme');
    if (!root) return [];
    return Array.prototype.filter.call(root.classList, function (name) {
      return name.indexOf('hxoFlag-') === 0;
    });
  }

  function buildRow(row, isCurrent) {
    var item = document.createElement('li');

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'kk-currency-option';
    button.setAttribute('role', 'option');
    button.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
    if (isCurrent) button.classList.add('is-current');

    /* The flag is rendered exactly as BUCKS renders it in the closed button:
       `<div class="hxoFlag hxoFlag-INR">`, a sprite slice positioned by the
       currency code. Cloning the node did NOT work, because the sprite's
       background-image comes from `.hxoFlag-traditional .hxoFlag` (or
       `-modern`), a class that sits on the `.hxoSelectTheme` ROOT - see
       flagStyleClasses(), which copies it onto our panel so the rule
       resolves. Nothing here sets width/height: the sprite rows are
       positioned in percentages, so changing the box size shifts which flag
       shows. */
    var flag = document.createElement('span');
    flag.className = 'kk-currency-option__flag';
    flag.setAttribute('aria-hidden', 'true');

    var sprite = document.createElement('span');
    sprite.className = 'hxoFlag hxoFlag-' + row.code;
    flag.appendChild(sprite);
    button.appendChild(flag);

    var text = document.createElement('span');
    text.className = 'kk-currency-option__text';

    var name = document.createElement('span');
    name.className = 'kk-currency-option__name';
    name.textContent = row.name;
    text.appendChild(name);

    var meta = document.createElement('span');
    meta.className = 'kk-currency-option__meta';
    meta.textContent = row.symbol && row.symbol !== row.code
      ? row.code + ' · ' + row.symbol
      : row.code;
    text.appendChild(meta);

    button.appendChild(text);

    var tick = document.createElement('span');
    tick.className = 'kk-currency-option__check';
    tick.setAttribute('aria-hidden', 'true');
    tick.textContent = '✓';
    button.appendChild(tick);

    button.addEventListener('click', function (event) {
      event.stopPropagation();
      // Delegate to BUCKS so its own switch, persistence and re-conversion run.
      row.source.click();
      closeAll();
      if (typeof window.kankatalaBucksRerender === 'function') window.kankatalaBucksRerender();
    });

    item.appendChild(button);
    return item;
  }

  function fill(listEl) {
    if (!listEl) return;
    var rows = bucksRows();
    if (rows.length === 0) return;

    var current = currentCode();
    // Current currency first, the rest in BUCKS's order.
    rows.sort(function (a, b) {
      return (b.code === current) - (a.code === current);
    });

    var panel = listEl.closest('.kankatala-currency') || listEl;
    flagStyleClasses().forEach(function (name) { panel.classList.add(name); });

    listEl.textContent = '';
    rows.forEach(function (row) {
      listEl.appendChild(buildRow(row, row.code === current));
    });
  }

  function fillAll() {
    document.querySelectorAll('[data-kankatala-currency-list]').forEach(fill);
  }

  /* ---- Sheet (mobile / tablet) ---- */
  var lastTrigger = null;

  function sheet() { return document.querySelector('[data-kankatala-currency-sheet]'); }
  function overlay() { return document.querySelector('[data-kankatala-currency-overlay]'); }
  function menu() { return document.querySelector('[data-kankatala-currency-menu]'); }

  function openSheet(trigger) {
    var el = sheet();
    var veil = overlay();
    if (!el || !veil) return;
    // Open, never toggle: a second tap while open must do nothing harmful.
    if (!el.hidden) return;

    lastTrigger = trigger || null;
    fill(el.querySelector('[data-kankatala-currency-list]'));

    veil.hidden = false;
    el.hidden = false;
    // Next frame, so the transition runs from the off-screen state.
    requestAnimationFrame(function () {
      veil.classList.add('is-open');
      el.classList.add('is-open');
    });

    document.documentElement.classList.add('kankatala-currency-locked');
    if (lastTrigger) lastTrigger.classList.add('is-currency-open');

    var first = el.querySelector('.kk-currency-option');
    if (first) first.focus();
  }

  function closeSheet() {
    var el = sheet();
    var veil = overlay();
    if (!el || el.hidden) return;

    el.classList.remove('is-open');
    if (veil) veil.classList.remove('is-open');
    document.documentElement.classList.remove('kankatala-currency-locked');

    window.setTimeout(function () {
      el.hidden = true;
      if (veil) veil.hidden = true;
    }, 250);

    if (lastTrigger) {
      lastTrigger.classList.remove('is-currency-open');
      // Back to the drawer, focus on the tile that opened the sheet.
      if (typeof lastTrigger.focus === 'function') lastTrigger.focus();
      lastTrigger = null;
    }
  }

  function closeMenu() {
    var el = menu();
    if (el) el.hidden = true;
  }

  function closeAll() {
    closeSheet();
    closeMenu();
  }

  /* ---- Wiring ---- */
  function trackTrigger(trigger) {
    // Tag every trigger so ONE delegated listener on document can find it.
    // The tap handler is NOT bound here on purpose: BUCKS rebuilds its widget
    // asynchronously (on reConvert, on currency change, when the drawer
    // re-renders), and a listener bound to the element is lost with it. The
    // rescan would rebind, but a tap landing in the gap before the
    // MutationObserver callback hit a dead element - which is exactly the
    // "sometimes does not open" bug. Delegation cannot miss.
    trigger.setAttribute('data-currency-trigger', '');

    if (trigger.dataset.kankatalaCurrency) return;
    trigger.dataset.kankatalaCurrency = 'true';

    // Desktop: BUCKS keeps owning open/closed; mirror its `.active` class.
    var observer = new MutationObserver(function () {
      if (!isDesktop()) return;
      var el = menu();
      if (!el) return;

      var open = trigger.classList.contains('active') && !trigger.classList.contains('deactive');
      if (open) {
        /* Park the dropdown in the header <li>, NOT in trigger.parentNode.
           The trigger's parent is `.buckscc-select`, and BUCKS styles
           `.hxoSelectTheme .buckscc-select span { display:flex;
           align-items:center; margin:0 4px; max-height:20px }`. That is a
           (0,2,1) selector, so it beat our (0,2,0) rules and centred every
           row - our name, code and flag are all <span>. Mounting the panel
           on the <li> puts it outside `.buckscc-select` entirely, so none of
           BUCKS's descendant rules can reach it. The <li> is
           position: relative, so the right-edge anchoring is unchanged. */
        var mount = trigger.closest('.left-header--icon') || trigger.parentNode;
        if (mount && el.parentNode !== mount) {
          mount.appendChild(el);
        }
        fill(el.querySelector('[data-kankatala-currency-list]'));
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });
    observer.observe(trigger, { attributes: true, attributeFilter: ['class'] });
  }

  /* Teach Prestige's drawer that our sheet is "inside".

     Prestige's DialogElement wraps itself in the focus-trap library with
     `clickOutsideDeactivates: true`. Two of its methods decide what happens
     to a click outside the drawer:

       hideForOutsideClickTarget(target) -> !this.contains(target)
         true  => hide the drawer
       allowOutsideClickForTarget(target) -> false
         false => SWALLOW the click instead of letting it through

     Our sheet is mounted on <body>, so it is outside the drawer and hits
     both. That is the whole bug: the first tap on the sheet was swallowed
     (hence "tap twice"), and the drawer hid itself before our handler ran
     (hence "x closes the menu first"). stopPropagation could never fix it -
     focus-trap is not reacting to propagation, it is asking these two
     questions, and on touch it re-resolves the target with
     document.elementFromPoint, which is why Safari and Chrome differed.

     Both methods are documented as overridable, so they are wrapped rather
     than replaced: anything that is not ours keeps Prestige's behaviour. */
  function patchDrawer(drawer) {
    if (!drawer || drawer.dataset.kankatalaCurrencyPatched) return;
    if (typeof drawer.hideForOutsideClickTarget !== 'function') return;
    drawer.dataset.kankatalaCurrencyPatched = 'true';

    function isOurs(target) {
      return !!(
        target &&
        target.closest &&
        target.closest('[data-kankatala-currency-sheet], [data-kankatala-currency-overlay], [data-currency-trigger]')
      );
    }

    var hideFor = drawer.hideForOutsideClickTarget.bind(drawer);
    drawer.hideForOutsideClickTarget = function (target) {
      if (isOurs(target)) return false;
      return hideFor(target);
    };

    var allowFor = drawer.allowOutsideClickForTarget.bind(drawer);
    drawer.allowOutsideClickForTarget = function (target) {
      if (isOurs(target)) return true;
      return allowFor(target);
    };
  }

  function patchDrawers() {
    document.querySelectorAll('header-sidebar, .header-sidebar').forEach(patchDrawer);
  }

  function scan() {
    document.querySelectorAll('.buckscc-select-styled').forEach(trackTrigger);
    patchDrawers();
  }

  function init() {
    scan();
    patchDrawers();

    // BUCKS injects and re-injects its widget asynchronously.
    new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });

    /* ONE delegated listener for every trigger, now and in the future.
       `click` only - no touchstart/pointerdown - so a tap cannot fire twice.
       stopPropagation keeps BUCKS's own toggle and any outside-click handler
       from seeing the same tap and undoing the open. */
    document.addEventListener('click', function (event) {
      if (!event.target.closest) return;

      var trigger = event.target.closest('[data-currency-trigger]');
      if (trigger && !isDesktop()) {
        event.preventDefault();
        event.stopPropagation();
        openSheet(trigger);
        return;
      }

      if (event.target.closest('[data-kankatala-currency-close]')) {
        /* stopPropagation matters here: the sheet is mounted on <body>, so
           it is OUTSIDE Prestige's drawer. Without this the same tap carries
           on to the document, Prestige reads it as a click outside the
           drawer and closes the drawer too - which is the reported
           "x closes the background menu first". */
        event.preventDefault();
        event.stopPropagation();
        closeSheet();
        return;
      }

      var el = sheet();
      if (!el || el.hidden) return;

      // Inside the sheet: handled by the row's own listener. Stop here so
      // Prestige never sees it as a click outside its drawer.
      if (event.target.closest('[data-kankatala-currency-sheet]')) {
        event.stopPropagation();
        return;
      }

      // Outside-click close, with the trigger excluded so the tap that opens
      // the sheet cannot immediately close it again.
      if (trigger) return;
      closeSheet();
    }, true);

    /* The double tap. A tap fires pointerdown -> touchstart -> click. We only
       stopped `click`, so the earlier pointer events still reached Prestige's
       drawer and BUCKS's own trigger handler; by the time our click handler
       ran the state had already moved, and the first tap was swallowed. These
       swallow the whole gesture for our three surfaces before anything else
       sees it. Only propagation is stopped - never preventDefault - so
       scrolling inside the sheet still works. */
    ['pointerdown', 'mousedown', 'touchstart'].forEach(function (name) {
      document.addEventListener(name, function (event) {
        if (!event.target.closest) return;
        if (
          event.target.closest('[data-currency-trigger]') ||
          event.target.closest('[data-kankatala-currency-sheet]') ||
          event.target.closest('[data-kankatala-currency-overlay]')
        ) {
          event.stopPropagation();
        }
      }, true);
    });

    var veil = overlay();
    if (veil) {
      veil.addEventListener('click', function (event) {
        event.stopPropagation();
        closeSheet();
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeAll();
      if (event.key !== 'Tab') return;

      // Focus trap, sheet only (the desktop dropdown is not modal).
      var el = sheet();
      if (!el || el.hidden) return;

      var focusable = el.querySelectorAll('button:not([disabled])');
      if (focusable.length === 0) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // Swipe down to dismiss.
    var startY = null;
    var el = sheet();
    if (el) {
      el.addEventListener('touchstart', function (event) {
        startY = event.touches[0].clientY;
      }, { passive: true });

      el.addEventListener('touchend', function (event) {
        if (startY === null) return;
        var delta = event.changedTouches[0].clientY - startY;
        startY = null;
        if (delta > 60) closeSheet();
      }, { passive: true });
    }

    // A currency change re-renders BUCKS's markup; keep our rows in step.
    document.addEventListener('shopify:section:load', fillAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
